/**
 * Gift Auto-Add System - Working Implementation
 * Uses server-side embedded gift data for reliable operation
 */

class GiftManager {
  constructor() {
    // Check for both product page gifts and cart page gifts
    this.giftData = window.productGiftData || null;
    this.cartGifts = window.cartGiftData || [];
    this.addedGifts = new Set();
    this.removedGifts = new Set(); // Track manually removed gifts
    this.isProcessing = false;
    
    // Load removed gifts from session storage
    this.loadRemovedGifts();
    
    console.log('🎁 Gift Manager starting...', {
      productGift: this.giftData ? 'Found' : 'None',
      cartGifts: this.cartGifts.length || 0
    });
    
    if (this.giftData || this.cartGifts.length > 0) {
      this.init();
    }
    
    // Listen for cart item removals
    this.setupRemovalTracking();
  }
  
  loadRemovedGifts() {
    try {
      const saved = sessionStorage.getItem('removedGifts');
      if (saved) {
        this.removedGifts = new Set(JSON.parse(saved));
      }
    } catch (e) {
      console.log('Could not load removed gifts from storage');
    }
  }
  
  saveRemovedGifts() {
    try {
      sessionStorage.setItem('removedGifts', JSON.stringify([...this.removedGifts]));
    } catch (e) {
      console.log('Could not save removed gifts to storage');
    }
  }
  
  setupRemovalTracking() {
    // Track manual gift removals
    document.addEventListener('click', (event) => {
      const removeButton = event.target.closest('cart-remove-button, .cart-item__remove, [data-remove-cart-item]');
      if (removeButton) {
        const cartItem = removeButton.closest('.cart-item, tr');
        if (cartItem && cartItem.textContent.includes('_is_gift: true')) {
          // Extract gift variant ID from multiple possible sources
          let variantId = null;
          
          // Try to get variant ID from data attributes first
          const cartRemoveButton = cartItem.querySelector('cart-remove-button');
          if (cartRemoveButton) {
            const dataIndex = cartRemoveButton.getAttribute('data-index');
            if (dataIndex) {
              // Get variant ID from cart data
              const cartItemId = `CartItem-${dataIndex}`;
              const variantInput = document.querySelector(`#${cartItemId} input[name*="variant_id"], #${cartItemId} [data-variant-id]`);
              if (variantInput) {
                variantId = variantInput.value || variantInput.dataset.variantId;
              }
            }
          }
          
          // Fallback: extract from text content
          if (!variantId) {
            const giftProperties = cartItem.textContent.match(/_gift_variant_id['":\s]*(\d+)|variant_id['":\s]*(\d+)/);
            if (giftProperties) {
              variantId = giftProperties[1] || giftProperties[2];
            }
          }
          
          if (variantId) {
            this.removedGifts.add(variantId);
            this.saveRemovedGifts();
            console.log('🗑️ Gift manually removed, will not re-add:', variantId);
          }
        }
      }
    });
  }

  init() {
    if (this.giftData) {
      console.log('🎯 Product gift system active for:', this.giftData.productTitle);
      console.log('🎁 Product gift details:', {
        product: this.giftData.title,
        label: this.giftData.label,
        variantId: this.giftData.variantId
      });
    }
    
    if (this.cartGifts.length > 0) {
      console.log('🛒 Cart gifts detected:', this.cartGifts.length);
      this.cartGifts.forEach(gift => {
        console.log(`  - ${gift.title} (${gift.label}) for ${gift.triggerProductTitle}`);
      });
    }
    
    // Listen to cart updates
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source !== 'gift-manager') {
          this.handleCartUpdate(event);
        }
      });
    }
    
    // Listen for form submissions as backup
    document.addEventListener('submit', (event) => {
      if (event.target.matches('form[action*="/cart/add"]')) {
        setTimeout(() => this.checkForGifts(), 1500);
      }
    });
    
    // Check immediately for all gifts
    this.checkForGifts();
  }

  async checkForGifts() {
    if (this.isProcessing) return;
    
    try {
      this.isProcessing = true;
      console.log('🔍 Checking for all available gifts...');
      
      const cart = await this.fetchCart();
      
      // Check product page gift (if any)
      if (this.giftData) {
        await this.checkSingleGift(this.giftData, cart);
      }
      
      // Check cart page gifts
      for (const gift of this.cartGifts) {
        await this.checkSingleGift(gift, cart);
      }
      
    } catch (error) {
      console.error('❌ Error checking for gifts:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  async checkSingleGift(giftData, cart) {
    // Check if main product is in cart
    const mainProductInCart = cart.items.some(item => 
      item.product_id == (giftData.productId || giftData.triggerProductId)
    );
    
    // Check if gift is already in cart
    const giftInCart = cart.items.some(item => 
      item.variant_id == giftData.variantId && 
      item.properties && 
      item.properties._is_gift === 'true'
    );
    
    // Check if we already tried to add this gift
    const alreadyAttempted = this.addedGifts.has(giftData.variantId);
    
    // Check if this gift was manually removed by user
    const manuallyRemoved = this.removedGifts.has(giftData.variantId.toString());
    
    console.log(`📊 Gift "${giftData.title}" status:`, {
      mainProductInCart,
      giftInCart, 
      alreadyAttempted,
      manuallyRemoved,
      triggerProduct: giftData.productTitle || giftData.triggerProductTitle
    });
    
    if (mainProductInCart && !giftInCart && !alreadyAttempted && !manuallyRemoved) {
      console.log(`✅ Adding gift: ${giftData.title}`);
      await this.addGift(giftData);
    } else if (manuallyRemoved) {
      console.log(`⚠️ Gift was manually removed, respecting user choice: ${giftData.title}`);
    }
  }

  async addGift(giftData) {
    try {
      console.log('🛒 Adding gift to cart...', giftData.title);
      
      const formData = new FormData();
      formData.append('id', giftData.variantId);
      formData.append('quantity', '1');
      
      // Mark as gift
      formData.append('properties[_is_gift]', 'true');
      formData.append('properties[_gift_label]', giftData.label);
      formData.append('properties[_gift_for]', giftData.productTitle || giftData.triggerProductTitle);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Gift added successfully!', result);
        
        // Mark as added
        this.addedGifts.add(giftData.variantId);
        
        // Show notification
        this.showNotification(giftData);
        
        // Trigger cart update
        if (typeof publish !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'gift-manager',
            cartData: await this.fetchCart()
          });
        }
        
      } else {
        const error = await response.text();
        console.error('❌ Gift addition failed:', response.status, error);
      }
      
    } catch (error) {
      console.error('❌ Exception adding gift:', error);
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.status}`);
    }
    return response.json();
  }

  handleCartUpdate(event) {
    console.log('🔄 Cart updated, checking for gifts...', event.source);
    setTimeout(() => this.checkForGifts(), 500);
  }

  showNotification(giftData = this.giftData) {
    // Remove existing notification
    const existing = document.querySelector('.gift-notification');
    if (existing) existing.remove();
    
    // Get translations - try product data first, then cart translations, then fallback
    let viewCartText = 'Click to view cart →';
    if (giftData.viewCartText) {
      viewCartText = giftData.viewCartText;
    } else if (window.giftSystemTranslations && window.giftSystemTranslations.viewCartText) {
      viewCartText = window.giftSystemTranslations.viewCartText;
    }
    
    // Smart color system with contrast detection
    const rootStyles = getComputedStyle(document.documentElement);
    const badgeBackground = rootStyles.getPropertyValue('--color-badge-background').trim();
    const badgeForeground = rootStyles.getPropertyValue('--color-badge-foreground').trim();
    
    // Calculate relative luminance for contrast checking
    const getLuminance = (rgbString) => {
      if (!rgbString) return 1;
      const rgb = rgbString.split(',').map(n => {
        const val = parseInt(n.trim()) / 255;
        return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
      });
      if (rgb.length !== 3) return 1;
      return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
    };
    
    // Calculate contrast ratio (WCAG standard)
    const getContrastRatio = (lum1, lum2) => {
      const lighter = Math.max(lum1, lum2);
      const darker = Math.min(lum1, lum2);
      return (lighter + 0.05) / (darker + 0.05);
    };
    
    // Check if badge colors provide sufficient contrast
    let useCustomDesign = true;
    let bgColor, textColor, borderColor;
    
    if (badgeBackground && badgeForeground) {
      const bgLum = getLuminance(badgeBackground);
      const fgLum = getLuminance(badgeForeground);
      const contrast = getContrastRatio(bgLum, fgLum);
      
      console.log('🎨 Color analysis:', {
        background: badgeBackground,
        foreground: badgeForeground,
        contrast: contrast.toFixed(2),
        meetsWCAG: contrast >= 4.5
      });
      
      // WCAG AA requires 4.5:1 contrast ratio for normal text
      if (contrast >= 4.5) {
        useCustomDesign = false;
        bgColor = `rgb(${badgeBackground})`;
        textColor = `rgb(${badgeForeground})`;
        borderColor = `rgba(${badgeBackground}, 0.2)`;
      }
    }
    
    // Use beautiful custom design if badge colors don't work
    if (useCustomDesign) {
      // Modern dark purple gradient with excellent contrast
      bgColor = 'linear-gradient(135deg, #6b46c1 0%, #553c9a 100%)';
      textColor = '#ffffff';
      borderColor = 'rgba(107, 70, 193, 0.3)';
      console.log('🎯 Using custom purple design for guaranteed visibility');
    }
    
    // Modern design with guaranteed visibility
    const notification = document.createElement('div');
    notification.className = 'gift-notification';
    notification.innerHTML = `
      <div class="gift-notification-content">
        <div class="gift-notification-icon">
          <span class="gift-emoji">🎁</span>
        </div>
        <div class="gift-notification-main" onclick="this.closest('.gift-notification').click()">
          <div class="gift-notification-title">${giftData.label}</div>
          <div class="gift-notification-action">${viewCartText}</div>
        </div>
        <button class="gift-notification-close" onclick="this.closest('.gift-notification').remove()" aria-label="Close notification" tabindex="0">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    `;
    
    // Apply styling with guaranteed visibility
    Object.assign(notification.style, {
      background: bgColor,
      color: textColor,
      border: `1px solid ${borderColor}`
    });
    
    // Common modern styling
    Object.assign(notification.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '0',
      borderRadius: '16px',
      zIndex: '9999',
      fontFamily: 'inherit',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15), 0 2px 10px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05)',
      animation: 'giftSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
      cursor: 'pointer',
      minWidth: '320px',
      maxWidth: '400px',
      overflow: 'hidden',
      userSelect: 'none'
    });
    
    document.body.appendChild(notification);
    
    // Add click handler to navigate to cart
    notification.addEventListener('click', (e) => {
      if (e.target.classList.contains('gift-notification-close')) return;
      
      // Navigate to cart
      if (window.location.pathname.includes('/cart')) {
        // Already on cart page, just close notification
        notification.remove();
      } else {
        // Navigate to cart
        window.location.href = '/cart';
      }
    });
    
    // Auto remove after 6 seconds (longer for better UX)
    setTimeout(() => {
      if (notification.parentNode) {
        notification.style.animation = 'giftSlideOut 0.4s cubic-bezier(0.55, 0.085, 0.68, 0.53)';
        setTimeout(() => {
          if (notification.parentNode) {
            notification.remove();
          }
        }, 400);
      }
    }, 6000);
    
    console.log('🔔 Enhanced notification shown with theme colors');
  }
}

// Add modern notification styles
const style = document.createElement('style');
style.textContent = `
  @keyframes giftSlideIn {
    from { 
      transform: translateX(100%) translateY(-10px) scale(0.95); 
      opacity: 0; 
    }
    to { 
      transform: translateX(0) translateY(0) scale(1); 
      opacity: 1; 
    }
  }
  
  @keyframes giftSlideOut {
    from { 
      transform: translateX(0) translateY(0) scale(1); 
      opacity: 1; 
    }
    to { 
      transform: translateX(100%) translateY(-10px) scale(0.95); 
      opacity: 0; 
    }
  }
  
  @keyframes giftPulse {
    0%, 100% { 
      transform: scale(1); 
    }
    50% { 
      transform: scale(1.15); 
    }
  }
  
  /* Modern notification styles */
  .gift-notification {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    transition: all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
  }
  
  .gift-notification:hover {
    transform: translateY(-3px) scale(1.01);
    box-shadow: 0 15px 45px rgba(0, 0, 0, 0.2), 0 5px 15px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05) !important;
  }
  
  .gift-notification-content {
    display: flex;
    align-items: stretch;
    padding: 0;
    gap: 0;
    min-height: 80px;
  }
  
  .gift-notification-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(255, 255, 255, 0.1);
    border-right: 1px solid rgba(255, 255, 255, 0.15);
  }
  
  .gift-emoji {
    font-size: 28px;
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
    animation: giftPulse 3s ease-in-out infinite;
  }
  
  .gift-notification-main {
    flex: 1;
    padding: 16px 20px;
    cursor: pointer;
    transition: background 0.2s ease;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
  }
  
  .gift-notification-main:hover {
    background: rgba(255, 255, 255, 0.05);
  }
  
  .gift-notification-title {
    font-weight: 600;
    font-size: 15px;
    line-height: 1.3;
    color: inherit;
    text-shadow: 0 1px 2px rgba(0,0,0,0.1);
    margin: 0;
    word-break: break-word;
  }
  
  .gift-notification-action {
    font-weight: 500;
    font-size: 12px;
    line-height: 1.2;
    color: inherit;
    opacity: 0.85;
    margin-top: 4px;
    transform: translateY(0);
    transition: all 0.2s ease;
  }
  
  .gift-notification-main:hover .gift-notification-action {
    opacity: 1;
    transform: translateX(2px);
  }
  
  .gift-notification-close {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: inherit;
    opacity: 0.6;
    cursor: pointer;
    padding: 0;
    width: 44px;
    height: 100%;
    border-left: 1px solid rgba(255, 255, 255, 0.15);
    transition: all 0.2s ease;
    flex-shrink: 0;
  }
  
  .gift-notification-close:hover {
    background: rgba(255, 255, 255, 0.1);
    opacity: 1;
  }
  
  .gift-notification-close:focus {
    outline: 2px solid rgba(255, 255, 255, 0.3);
    outline-offset: -2px;
  }
  
  .gift-notification-close svg {
    width: 14px;
    height: 14px;
  }
  
  .gift-notification-close span {
    transform: scale(0.9);
    transition: transform 0.1s ease;
  }
  
  .gift-notification-close:hover span {
    transform: scale(1.1);
  }
  
  /* Mobile responsive */
  @media (max-width: 768px) {
    .gift-notification {
      top: 15px !important;
      right: 15px !important;
      left: 15px !important;
      min-width: unset !important;
      max-width: unset !important;
      width: auto !important;
    }
    
    .gift-notification-content {
      min-height: 70px;
    }
    
    .gift-notification-icon {
      padding: 16px;
    }
    
    .gift-emoji {
      font-size: 24px;
    }
    
    .gift-notification-main {
      padding: 12px 16px;
    }
    
    .gift-notification-title {
      font-size: 14px;
    }
    
    .gift-notification-action {
      font-size: 11px;
    }
    
    .gift-notification-close {
      width: 40px;
      font-size: 16px;
    }
  }
  
  /* Accessibility improvements */
  @media (prefers-reduced-motion: reduce) {
    .gift-notification {
      animation: none !important;
    }
    
    .gift-emoji {
      animation: none !important;
    }
    
    .gift-notification:hover {
      transform: none !important;
    }
  }
`;
document.head.appendChild(style);

// Initialize when ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new GiftManager());
} else {
  new GiftManager();
}